// ============================================================================
// useHomeWork — the rows and the three numbers for one view (item #650).
// useEffect loaders, no TanStack (TCSD-0168). Registered with the save path's
// refresh list so a save anywhere that touches these tables reloads Home.
// ============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../shared/supabase';
import { useAuth } from '../../shared/auth';
import { errorMessage } from '../../shared/errors';
import { useReader } from '../../shared/save-path';
import type { WorkScopeState } from '../../shared/scope';
import { useProjects } from '../project-work/hooks/use-project-data';
import { fromDb, projectRow, PROJECT_ITEM_TYPE, type HomeWorkDbRow, type WorkRow } from './home-work';

export interface HomeTiles {
  notSentTotal: number;
  notSentCount: number;
  startingThisWeek: number;
  openComplaints: number;
}

const NOT_SENT = ['Draft', 'Awaiting_AM_Input'];

export function useHomeWork(ws: WorkScopeState) {
  const { employee } = useAuth();
  const myEid = employee?.employee_id ?? null;
  const [dbRows, setDbRows] = useState<WorkRow[]>([]);
  const [tiles, setTiles] = useState<HomeTiles | null>(null);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [cleaners, setCleaners] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((n) => n + 1), []);

  const projectFilter = useMemo(
    () => ({
      stages: NOT_SENT,
      scope: !ws.ready
        ? { facilityIds: [] as string[] }
        : ws.scope === 'all'
          ? null
          : { facilityIds: ws.facilityIds ?? [], orCreatedBy: ws.scope === 'mine' ? myEid : null },
    }),
    [ws.ready, ws.scope, ws.facilityIds, myEid],
  );
  const projects = useProjects(projectFilter);

  useEffect(() => {
    if (!ws.ready || !ws.scope) return;
    let cancelled = false;
    (async () => {
      setError(null);
      try {
        const [work, t, dis] = await Promise.all([
          supabase.rpc('get_home_work', { p_scope: ws.scope! }),
          supabase.rpc('get_home_tiles', { p_scope: ws.scope! }),
          supabase.from('my_work_dismissals').select('item_type, item_id').eq('item_type', PROJECT_ITEM_TYPE),
        ]);
        if (cancelled) return;
        if (work.error) throw work.error;
        if (t.error) throw t.error;
        if (dis.error) throw dis.error;
        setDbRows(((work.data ?? []) as HomeWorkDbRow[]).map(fromDb));
        const tr = ((t.data ?? []) as { not_sent_total: number; not_sent_count: number; starting_this_week: number; open_complaints: number }[])[0];
        setTiles(
          tr
            ? {
                notSentTotal: Number(tr.not_sent_total ?? 0),
                notSentCount: tr.not_sent_count ?? 0,
                startingThisWeek: tr.starting_this_week ?? 0,
                openComplaints: tr.open_complaints ?? 0,
              }
            : null,
        );
        setDismissed(new Set(((dis.data ?? []) as { item_type: string; item_id: string }[]).map((d) => d.item_id)));
      } catch (e) {
        if (!cancelled) setError(errorMessage(e, 'Could not load your work.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ws.ready, ws.scope, tick]);

  // The main cleaner for each building a project card sits on.
  const projectFacilityIds = useMemo(
    () => Array.from(new Set(projects.data.map((p) => p.facility_id).filter((x): x is string => !!x))).sort(),
    [projects.data],
  );
  const facKey = projectFacilityIds.join(',');
  useEffect(() => {
    if (projectFacilityIds.length === 0) return;
    let cancelled = false;
    (async () => {
      const { data, error: e } = await supabase
        .from('service_assignments')
        .select('facility_id, created_at, service_providers ( partner_name )')
        .eq('assignment_status', 'Active')
        .eq('service_code', 'MAIN_CLEAN')
        .in('facility_id', projectFacilityIds)
        .order('created_at', { ascending: true });
      if (cancelled || e) return;
      const m = new Map<string, string>();
      for (const r of (data ?? []) as unknown as { facility_id: string; service_providers: { partner_name: string | null } | null }[]) {
        if (r.service_providers?.partner_name) m.set(r.facility_id, r.service_providers.partner_name);
      }
      setCleaners(m);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facKey]);

  const rows = useMemo<WorkRow[]>(() => {
    const proj = projects.data
      .filter((p) => !dismissed.has(`UNSENT:${p.project_id}`))
      .map((p) => projectRow(p, cleaners, myEid));
    return [...dbRows, ...proj];
  }, [dbRows, projects.data, dismissed, cleaners, myEid]);

  const projectsRefetch = projects.refetch;
  const reload = useCallback(() => {
    refetch();
    projectsRefetch();
  }, [refetch, projectsRefetch]);
  useReader(
    ['projects', 'project_services', 'facilities', 'service_assignments', 'notification_recipients', 'my_work_dismissals', 'facility_feedback', 'new_account_sop', 'record_routing'],
    reload,
  );

  return {
    rows,
    tiles,
    loading: loading || projects.isLoading || !ws.ready,
    error: error ?? projects.error,
    reload,
  };
}
